import { Actions, reducer } from './reducer';
import {
	AuthDocumentType,
	ConsultaMedicamentosState,
	ConsultaMedicamentosFormParams,
} from 'utils/interfaces';
import { createContext, useContext, useReducer } from 'react';
import { API } from 'utils';

export const ConsultaMedicamentosInitialState: ConsultaMedicamentosState = {
	page: 1,
	perPage: 5,
	view: 'form',
	totalPages: 0,
	botiquines: [],
	botiquin: undefined,
	notAvailable: false,
	showMiReceta: false,
	botiquinesInPage: [],
	isFormLoading: false,
	showInfoReceta: false,
	infoReceta: undefined,
	statusMessage: undefined,
	reservaConfirmationData: undefined,
	reservaExistente: undefined,
	showModalReservaExistente: false,
	venceAhora: false,
};

interface ContextState extends ConsultaMedicamentosState {
	nextPage: () => void;
	resetPage: () => void;
	resetState: () => void;
	setShowMiReceta: () => void;
	dispatch: React.Dispatch<Actions>;
	getBotiquines: () => Promise<void>;
	setStatusMessage: (payload: string) => void;
	filterByDepartamento: (departamento: string) => void;
	reservarMedicamento: (
		setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
	) => Promise<void>;
	onFormSubmit: (
		data: ConsultaMedicamentosFormParams,
		authType?: AuthDocumentType
	) => Promise<void>;
}

const ConsultaMedicamentosContext = createContext({} as ContextState);

interface Props {
	children: JSX.Element[];
}

export function ConsultaMedicamentosProvider({ children }: Props) {
	const [state, dispatch] = useReducer(
		reducer,
		ConsultaMedicamentosInitialState
	);

	const resetState = () => {
		dispatch({ type: 'reset' });
	};

	const setFormLoading = (payload: boolean) => {
		dispatch({ type: 'setFormLoading', payload });
	};

	const setStatusMessage = (payload: string) => {
		dispatch({
			payload,
			type: 'setStatusMessage',
		});
	};

	const onFormSubmit = async (
		params: ConsultaMedicamentosFormParams,
		authType?: AuthDocumentType
	) => {
		setFormLoading(true);
		try {
			const [response, reserva] = await Promise.all([
				API.getInfoReceta(params),
				API.consultaReserva(params),
			]);
			const { mensaje } = response.data;
			if (response.status === 418) {
				const message =
					authType === 'dui' && mensaje === '-1' ? 'duiNotFound' : mensaje;
				setStatusMessage(message);
			}

			if (response.status === 200) {
				dispatch({
					type: 'setInfoReceta',
					payload: {
						...response.data,
						dui: params.dui,
						numReceta: params.receta,
						idAfiliado: params.afiliado,
					},
				});

				const { message, dto, success } = reserva.data;

				if (dto && dto.venceAhora) {
					setStatusMessage('vence_ahora');
					dispatch({ type: 'venceAhora' });
				}

				if (success) {
					dispatch({
						type: 'setReservaExistente',
						payload: dto,
					});

					if (['-5', '-6', '-7'].includes(message)) {
						/*
							El prefijo "rm" es para identificar que este es
							un mensaje del endpoint Consulta de Reserva de Medicamentos 
						*/
						setStatusMessage(`rm${message}`);
					} else {
						dispatch({ type: 'setModalReservaExistente', payload: true });
					}
				} else {
					dispatch({
						type: 'setShowInfoReceta',
						payload: true,
					});
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setFormLoading(false);
		}
	};

	const getBotiquines = async () => {
		if (!state.infoReceta?.idMedicamento || !state.infoReceta.log) {
			return;
		}
		const { idMedicamento, log, cantidad } = state.infoReceta;

		dispatch({ type: 'setShowInfoReceta', payload: false });
		dispatch({ type: 'setView', payload: 'loading' });

		try {
			const response = await API.getBotiquines(
				idMedicamento,
				log,
				parseInt(cantidad, 10)
			);

			if (response.status !== 200) {
				throw new Error('Error getBotiquines');
			}

			if (response.data && response.data.length) {
				dispatch({ type: 'setBotiquines', payload: response.data });
			} else {
				dispatch({ type: 'setNotAvailable' });
			}
		} catch (error) {
			console.error(error);
			resetState();
		} finally {
			dispatch({ type: 'setReservaExistente' });
			dispatch({ type: 'setStatusMessage' });
		}
	};

	const nextPage = () => {
		dispatch({ type: 'nextPage' });
	};

	const resetPage = () => {
		dispatch({ type: 'resetPage' });
	};

	const filterByDepartamento = (payload: string) => {
		dispatch({
			payload,
			type: 'filterByDepartamento',
		});
	};

	const setShowMiReceta = () => {
		dispatch({ type: 'setShowMiReceta' });
	};

	const reservarMedicamento = async (
		setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
	) => {
		const { botiquin, infoReceta } = state;
		if (!botiquin || !infoReceta) {
			return;
		}
		try {
			setIsLoading(true);
			const { data } = await API.reservarMedicamento(botiquin, infoReceta);
			dispatch({ type: 'setBotiquinForReservation' });
			if (data.success) {
				dispatch({
					type: 'setReservaConfirmationData',
					payload: {
						receta: infoReceta,
						...data.dto,
					},
				});
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const value = {
		...state,
		dispatch,
		nextPage,
		resetPage,
		resetState,
		onFormSubmit,
		getBotiquines,
		setShowMiReceta,
		setStatusMessage,
		reservarMedicamento,
		filterByDepartamento,
	};

	return (
		<ConsultaMedicamentosContext.Provider value={value}>
			{children}
		</ConsultaMedicamentosContext.Provider>
	);
}

export function useConsultaMedicamentos() {
	return useContext(ConsultaMedicamentosContext);
}
